import React, { useEffect, useRef, useState } from 'react';
import FeedbackPanel from './FeedbackPanel';
import { FeedbackEntityType } from '../../api/feedbackApi';
import { useFeedbackCounts, feedbackCacheKey } from '../../contexts/FeedbackCountsContext';
import { FeedbackCountEntry } from '../../api/feedbackApi';
import { OPEN_SURFACE_EVENT, consumePendingOpen, feedbackOpenToken } from '../../utils/feedbackOpen';
import { useBrand } from '../../contexts/BrandContext';

/**
 * The one feedback affordance for Phase 2 surfaces: chat glyph + ink count
 * badge + an anchored FeedbackPanel, in a single drop-in. Same icon and
 * behavior on every surface by construction.
 *
 * Brand comes from context unless overridden — feedback on universal
 * entities (a global template, a universal tactic) still lives in the
 * viewing brand's conversation.
 */
interface FeedbackButtonProps {
  entityType: FeedbackEntityType;
  entityKey: string;
  brandId?: number;
  className?: string;
  title?: string;
  /** Icon palette for the host surface (dropdown ink vs white cards). */
  tone?: 'dark' | 'light';
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  entityType,
  entityKey,
  brandId: brandIdProp,
  className = '',
  title = 'Feedback',
  tone = 'dark',
}) => {
  const { currentBrand } = useBrand();
  const brandId = brandIdProp ?? (currentBrand ? Number(currentBrand.id) : undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState<FeedbackCountEntry | null>(null);
  const feedbackCounts = useFeedbackCounts();

  useEffect(() => {
    if (brandId == null) return;
    let alive = true;
    feedbackCounts.getCount(brandId, entityType, entityKey)
      .then((e) => { if (alive) setEntry(e); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, entityType, entityKey]);

  // Deep-link arrival: ?open=<our token>&feedback=1 → scroll here + open.
  useEffect(() => {
    const token = feedbackOpenToken(entityType, entityKey);
    const tryConsume = () => {
      const hit = consumePendingOpen(token);
      if (!hit) return;
      buttonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (hit.feedback) setOpen(true);
    };
    // Slight delay: host dropdowns open on the same signal and need a
    // paint before our button exists at its final position.
    const t = window.setTimeout(tryConsume, 150);
    const onEvt = () => window.setTimeout(tryConsume, 150);
    window.addEventListener(OPEN_SURFACE_EVENT, onEvt);
    return () => { window.clearTimeout(t); window.removeEventListener(OPEN_SURFACE_EVENT, onEvt); };
  }, [entityType, entityKey]);

  if (brandId == null) return null;

  const live = feedbackCounts.counts[feedbackCacheKey(brandId, entityType, entityKey)];
  const effective = live ?? entry ?? { count: 0, resolved: false };
  const displayCount = effective.count;
  const isResolved = effective.resolved;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseDown={(e) => e.stopPropagation()}
        className={`relative p-1 rounded transition-colors ${
          tone === 'light'
            ? open ? 'text-blue-500 bg-blue-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
            : open ? 'text-white' : 'text-white/50 hover:text-white'
        } ${className}`}
        title={title}
      >
        {/* Three states: empty outline = no thread; outline + count =
            open items; filled white = resolved. */}
        <svg
          className="w-4 h-4"
          fill={isResolved ? 'currentColor' : 'none'}
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={isResolved ? { color: '#fff' } : undefined}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        {!isResolved && displayCount > 0 && (
          <span className="tend-chantab-badge">{displayCount > 99 ? '99+' : displayCount}</span>
        )}
      </button>
      {open && (
        <FeedbackPanel
          entityType={entityType}
          entityKey={entityKey}
          brandId={brandId}
          layout="anchored"
          anchorEl={buttonRef.current}
          onClose={() => setOpen(false)}
          onCountChange={(d) => {
            setEntry((e) => ({
              count: Math.max(0, (e?.count ?? 0) + d),
              resolved: d > 0 ? false : e?.resolved ?? false,
            }));
            feedbackCounts.bump(brandId, entityType, entityKey, d);
          }}
          onThreadChange={(resolved) => {
            setEntry((e) => ({ count: e?.count ?? 0, resolved }));
            feedbackCounts.setResolved(brandId, entityType, entityKey, resolved);
          }}
        />
      )}
    </>
  );
};

export default FeedbackButton;
